import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  TablePagination,
} from '@mui/material';
import {
  History,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SwapHoriz,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

interface Transfer {
  id: string;
  type: string;
  protocol?: string;
  transactionId?: string;
  amount: number;
  fee: number;
  beneficiary: string;
  beneficiaryDocument?: string;
  bank: string;
  agency?: string;
  account?: string;
  description?: string;
  scheduledDate?: string;
  processedDate?: string;
  status: string;
  statusReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface TransferMeta {
  total: number;
  limit: number;
  offset: number;
}

const TransferHistoryPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [meta, setMeta] = useState<TransferMeta>({ total: 0, limit: 50, offset: 0 });
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, any> = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;

      const response = await api.get('/transfers', { params });
      const success = response?.success ?? response?.data?.success;
      const data = response?.data ?? response;

      if (success) {
        setTransfers(data?.data || []);
        setMeta(data?.meta || { total: 0, limit: rowsPerPage, offset: 0 });
      } else {
        setError(data?.error?.message || 'Erro ao carregar transferências');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.error?.message || err.message || 'Erro ao carregar transferências');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter, typeFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchTransfers();
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchTransfers();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'concluida':
        return <Chip label="Concluída" color="success" size="small" />;
      case 'pending':
      case 'pendente':
        return <Chip label="Processando" color="warning" size="small" />;
      case 'failed':
      case 'falha':
        return <Chip label="Falhou" color="error" size="small" />;
      case 'cancelled':
      case 'cancelada':
        return <Chip label="Cancelada" color="default" size="small" />;
      case 'scheduled':
      case 'agendada':
        return <Chip label="Agendada" color="info" size="small" />;
      default:
        return <Chip label={status || 'Desconhecido'} size="small" />;
    }
  };

  const getTypeChip = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'PIX':
        return <Chip label="PIX" color="success" size="small" variant="outlined" />;
      case 'TED':
        return <Chip label="TED" color="primary" size="small" variant="outlined" />;
      case 'DOC':
        return <Chip label="DOC" color="info" size="small" variant="outlined" />;
      case 'INTERNA':
      case 'INTERNAL':
        return <Chip label="Interna" color="secondary" size="small" variant="outlined" />;
      default:
        return <Chip label={type || 'N/A'} size="small" variant="outlined" />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            <History sx={{ mr: 1, verticalAlign: 'middle' }} />
            Histórico de Transferências
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte todas as transferências realizadas
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchTransfers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Data Início"
              type="date"
              InputLabelProps={{ shrink: true }}
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Data Fim"
              type="date"
              InputLabelProps={{ shrink: true }}
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="">Todos</option>
              <option value="completed">Concluída</option>
              <option value="pending">Pendente</option>
              <option value="failed">Falha</option>
              <option value="cancelled">Cancelada</option>
              <option value="scheduled">Agendada</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Tipo"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="">Todos</option>
              <option value="PIX">PIX</option>
              <option value="TED">TED</option>
              <option value="DOC">DOC</option>
              <option value="INTERNA">Interna</option>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ height: '40px' }}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Beneficiário</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Data/Hora</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : transfers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <SwapHoriz sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" display="block">
                      Nenhuma transferência encontrada
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      As transferências realizadas aparecerão aqui
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers.map((transfer) => (
                  <TableRow key={transfer.id} hover>
                    <TableCell>{getTypeChip(transfer.type)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {transfer.beneficiary || '-'}
                      </Typography>
                      {transfer.beneficiaryDocument && (
                        <Typography variant="caption" color="text.secondary">
                          {transfer.beneficiaryDocument}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transfer.bank || '-'}
                      </Typography>
                      {(transfer.agency || transfer.account) && (
                        <Typography variant="caption" color="text.secondary">
                          Ag: {transfer.agency || '-'} / Cc: {transfer.account || '-'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ color: 'error.main', fontWeight: 'bold' }}
                      >
                        - {formatCurrency(transfer.amount)}
                      </Typography>
                      {transfer.fee > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Taxa: {formatCurrency(transfer.fee)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(transfer.createdAt)}
                      </Typography>
                      {transfer.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {transfer.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{getStatusChip(transfer.status)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {transfers.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={meta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        )}
      </Paper>
    </Box>
  );
};

export default TransferHistoryPage;
